import React, { useState, useEffect } from 'react';
import {
  PlayIcon,
  StopIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  ClockIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  CogIcon
} from '@heroicons/react/24/outline';
import { automationApi } from '@/services/api';
import AutomationSessionForm from '@/components/Automation/AutomationSessionForm';
import AutomationSessionDetails from '@/components/Automation/AutomationSessionDetails';

interface AutomationSession {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  isPaperTrading: boolean;
  currentStatus: 'running' | 'stopped' | 'paused' | 'error';
  strategies: string[];
  executionInterval: number;
  maxDailyExecutions: number;
  strategyExecutionMode: string;
  maxDailyVolume: number;
  maxPositionSizePct: number;
  stopLossPct: number | null;
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  lastExecutionAt: string | null;
  nextExecutionAt: string | null;
  totalTrades: number;
  profitableTrades: number;
  totalVolume: number;
  totalProfitLoss: number;
  createdAt: string;
  updatedAt: string;
  lastErrorMessage?: string;
  lastErrorAt?: string;
}

const AutomationPage: React.FC = () => {
  const [sessions, setSessions] = useState<AutomationSession[]>([]);
  const [selectedSession, setSelectedSession] = useState<AutomationSession | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    isActive: undefined as boolean | undefined,
    sortBy: 'created_at' as string,
    sortOrder: 'DESC' as string
  });

  // Stats for dashboard
  const [stats, setStats] = useState({
    totalSessions: 0,
    activeSessions: 0,
    runningSessions: 0,
    totalExecutions24h: 0
  });

  useEffect(() => {
    loadSessions();
  }, [filters]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await automationApi.getSessions({
        status: filters.status || undefined,
        isActive: filters.isActive,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
        limit: 50
      });

      const sessionsData = (response as any).data?.sessions || [];
      setSessions(sessionsData);
      
      // Calculate stats
      setStats({
        totalSessions: sessionsData.length,
        activeSessions: sessionsData.filter((s: AutomationSession) => s.isActive).length,
        runningSessions: sessionsData.filter((s: AutomationSession) => s.currentStatus === 'running').length,
        totalExecutions24h: sessionsData.reduce((sum: number, s: AutomationSession) => sum + s.totalExecutions, 0)
      });

    } catch (err: any) {
      console.error('Failed to load sessions:', err);
      setError(err.message || 'Failed to load automation sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (sessionData: any) => {
    try {
      const response = await automationApi.createSession(sessionData);
      const newSession = (response as any).data?.session;
      
      setSessions(prev => [newSession, ...prev]);
      setShowCreateForm(false);
      
      // Auto-select the new session
      setTimeout(() => {
        setSelectedSession(newSession);
      }, 100);

    } catch (err: any) {
      console.error('Failed to create session:', err);
      throw err;
    }
  };

  const handleStartSession = async (sessionId: string) => {
    try {
      await automationApi.startSession(sessionId);
      await loadSessions(); // Refresh to get updated status
    } catch (err: any) {
      console.error('Failed to start session:', err);
      setError(err.message || 'Failed to start session');
    }
  };

  const handleStopSession = async (sessionId: string) => {
    try {
      await automationApi.stopSession(sessionId);
      await loadSessions(); // Refresh to get updated status
    } catch (err: any) {
      console.error('Failed to stop session:', err);
      setError(err.message || 'Failed to stop session');
    }
  };

  const handleDeleteSession = async (sessionId: string) => {
    if (!confirm('Are you sure you want to delete this automation session?')) {
      return;
    }

    try {
      await automationApi.deleteSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
      }
    } catch (err: any) {
      console.error('Failed to delete session:', err);
      setError(err.message || 'Failed to delete session');
    }
  };

  const handleExecuteSession = async (sessionId: string) => {
    try {
      await automationApi.executeSession(sessionId);
      await loadSessions(); // Refresh to get updated status
    } catch (err: any) {
      console.error('Failed to execute session:', err);
      setError(err.message || 'Failed to execute session');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
    case 'running':
      return <PlayIcon className="h-4 w-4 text-green-500" />;
    case 'stopped':
      return <StopIcon className="h-4 w-4 text-gray-500" />;
    case 'error':
      return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
    case 'paused':
      return <ClockIcon className="h-4 w-4 text-yellow-500" />;
    default:
      return <StopIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
    case 'running':
      return 'bg-success-subtle text-success-custom';
    case 'stopped':
      return 'bg-secondary-custom text-secondary-custom';
    case 'error':
      return 'bg-error-subtle text-error-custom';
    case 'paused':
      return 'bg-warning-subtle text-warning-custom';
    default:
      return 'bg-secondary-custom text-secondary-custom';
    }
  };

  const formatNextExecution = (nextExecution: string | null) => {
    if (!nextExecution) return 'Not scheduled';
    
    const now = new Date();
    const next = new Date(nextExecution);
    const diff = next.getTime() - now.getTime();
    
    if (diff < 0) return 'Overdue';
    
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 60) return `${minutes}m`;
    
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ${minutes % 60}m`;
    
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h`;
  };

  return (
    <div className="min-h-screen bg-secondary-custom p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-1 font-bold text-primary-custom">Automated Trading</h1>
            <p className="text-body text-secondary-custom mt-1">
              Manage and monitor your automated trading sessions
            </p>
          </div>
          
          <div className="flex space-x-3">
            <button
              onClick={() => loadSessions()}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-secondary-custom rounded-md shadow-sm text-body font-medium text-secondary-custom card hover-bg-secondary-custom disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-body font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Automation
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-error-subtle border-l-4 border-error-subtle p-4">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-error-custom" />
              <div className="ml-3">
                <p className="text-body text-error-custom">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="mt-2 text-caption text-error-custom hover:opacity-75 underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card rounded-lg shadow p-6">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-body font-medium text-secondary-custom">Total Sessions</p>
                <p className="text-data-large font-bold text-primary-custom">{stats.totalSessions}</p>
              </div>
            </div>
          </div>

          <div className="card rounded-lg shadow p-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-body font-medium text-secondary-custom">Active Sessions</p>
                <p className="text-data-large font-bold text-primary-custom">{stats.activeSessions}</p>
              </div>
            </div>
          </div>

          <div className="card rounded-lg shadow p-6">
            <div className="flex items-center">
              <PlayIcon className="h-8 w-8 text-indigo-500" />
              <div className="ml-4">
                <p className="text-body font-medium text-secondary-custom">Running Now</p>
                <p className="text-data-large font-bold text-primary-custom">{stats.runningSessions}</p>
              </div>
            </div>
          </div>

          <div className="card rounded-lg shadow p-6">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-purple-500" />
              <div className="ml-4">
                <p className="text-body font-medium text-secondary-custom">Executions</p>
                <p className="text-data-large font-bold text-primary-custom">{stats.totalExecutions24h}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sessions List */}
          <div className="lg:col-span-2">
            <div className="card rounded-lg shadow">
              {/* Filters */}
              <div className="border-b border-primary-custom p-4">
                <div className="flex flex-wrap gap-4">
                  <select
                    value={filters.status}
                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                    className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom block w-32 rounded-md shadow-sm"
                  >
                    <option value="">All Status</option>
                    <option value="running">Running</option>
                    <option value="stopped">Stopped</option>
                    <option value="paused">Paused</option>
                    <option value="error">Error</option>
                  </select>

                  <select
                    value={filters.isActive === undefined ? '' : filters.isActive.toString()}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      isActive: e.target.value === '' ? undefined : e.target.value === 'true'
                    }))}
                    className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom block w-32 rounded-md shadow-sm"
                  >
                    <option value="">All Sessions</option>
                    <option value="true">Active Only</option>
                    <option value="false">Inactive Only</option>
                  </select>

                  <select
                    value={`${filters.sortBy}_${filters.sortOrder}`}
                    onChange={(e) => {
                      const [sortBy, sortOrder] = e.target.value.split('_');
                      setFilters(prev => ({ ...prev, sortBy, sortOrder }));
                    }}
                    className="form-input form-bg-custom form-border-custom form-text-custom focus-border-custom focus-ring-custom block w-40 rounded-md shadow-sm"
                  >
                    <option value="created_at_DESC">Newest First</option>
                    <option value="created_at_ASC">Oldest First</option>
                    <option value="name_ASC">Name A-Z</option>
                    <option value="name_DESC">Name Z-A</option>
                    <option value="last_execution_at_DESC">Last Execution</option>
                  </select>
                </div>
              </div>

              {/* Sessions Table */}
              <div className="overflow-hidden">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-body text-secondary-custom">Loading sessions...</p>
                  </div>
                ) : sessions.length === 0 ? (
                  <div className="p-8 text-center">
                    <CogIcon className="mx-auto h-12 w-12 text-tertiary-custom" />
                    <h3 className="mt-2 text-body font-medium text-primary-custom">No automation sessions</h3>
                    <p className="mt-1 text-body text-secondary-custom">
                      Get started by creating your first automated trading session.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => setShowCreateForm(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-body font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        New Automation
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="divide-y divide-primary-custom">
                    {sessions.map((session) => (
                      <div
                        key={session.id}
                        className={`p-4 hover-bg-secondary-custom cursor-pointer transition-colors ${
                          selectedSession?.id === session.id ? 'bg-info-subtle border-l-4 border-blue-500' : ''
                        }`}
                        onClick={() => setSelectedSession(session)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                {getStatusIcon(session.currentStatus)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-body font-medium text-primary-custom truncate">
                                  {session.name}
                                </p>
                                <div className="flex items-center space-x-4 text-caption text-secondary-custom">
                                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-caption font-medium ${getStatusColor(session.currentStatus)}`}>
                                    {session.currentStatus.toUpperCase()}
                                  </span>
                                  <span>{session.strategies.length} strategies</span>
                                  <span>Every {session.executionInterval}min</span>
                                  {session.isPaperTrading && (
                                    <span className="text-warning-custom">Paper Trading</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <div className="text-right text-caption text-secondary-custom">
                              <div>Next: {formatNextExecution(session.nextExecutionAt)}</div>
                              <div>{session.totalExecutions} executions</div>
                            </div>
                            
                            <div className="flex items-center space-x-1">
                              {session.currentStatus === 'running' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStopSession(session.id);
                                  }}
                                  className="p-1 text-tertiary-custom hover:text-red-600"
                                  title="Stop Session"
                                >
                                  <StopIcon className="h-4 w-4" />
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartSession(session.id);
                                  }}
                                  className="p-1 text-tertiary-custom hover:text-green-600"
                                  title="Start Session"
                                >
                                  <PlayIcon className="h-4 w-4" />
                                </button>
                              )}
                              
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSession(session.id);
                                }}
                                className="p-1 text-tertiary-custom hover:text-red-600"
                                title="Delete Session"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {session.lastErrorMessage && (
                          <div className="mt-2 text-caption text-error-custom">
                            Error: {session.lastErrorMessage}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Session Details */}
          <div className="lg:col-span-1">
            {selectedSession ? (
              <AutomationSessionDetails 
                session={selectedSession}
                onExecute={handleExecuteSession}
                onStart={handleStartSession}
                onStop={handleStopSession}
                onDelete={handleDeleteSession}
              />
            ) : (
              <div className="card rounded-lg shadow p-6">
                <div className="text-center text-secondary-custom">
                  <EyeIcon className="mx-auto h-12 w-12 text-tertiary-custom" />
                  <p className="mt-2 text-body">Select a session to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Session Modal */}
        {showCreateForm && (
          <AutomationSessionForm
            onSubmit={handleCreateSession}
            onCancel={() => setShowCreateForm(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AutomationPage; 